const cleanText = (value) => String(value || '').trim();

const isArrayLikeType = (type) => cleanText(type)
  .toLowerCase()
  .split(/[^a-z0-9_]+/)
  .filter(Boolean)
  .includes('array');

const conditionPathSegments = (path = '') => {
  const parts = cleanText(path)
    .split('.')
    .map((item) => cleanText(item))
    .filter(Boolean);
  if (parts[0] === 'root' && parts[1]) {
    return [`root.${parts[1]}`, ...parts.slice(2)];
  }
  return parts;
};

const normalizeRootNamespacePath = (path = '') => conditionPathSegments(path).join('.');

const sanitizeConditionPathPart = (value) => {
  const sanitized = cleanText(value).replace(/[\[\]]+/g, '');
  if (!sanitized) return '';
  return /[\p{L}\p{N}_]/u.test(sanitized) ? sanitized : '';
};

const conditionVariableTypeLabel = (type) => {
  const normalized = cleanText(type).toLowerCase();
  if (['int', 'integer', 'number', 'float', 'double'].includes(normalized)) return 'numero';
  if (['bool', 'boolean'].includes(normalized)) return 'booleano';
  if (normalized === 'array') return 'lista';
  if (['object', 'json'].includes(normalized)) return 'objeto';
  return normalized || 'valor';
};

const conditionVariablePathMetadata = (value, type = '') => {
  const arrayPaths = new Set();
  const parts = [];
  let previousPath = '';

  cleanText(value).replace(/^\$\.?/, '').split('.').forEach((rawPart) => {
    const normalizedPart = cleanText(rawPart);
    if (!normalizedPart) return;
    if (/^\d+$/.test(normalizedPart) || /^\[\d*\]$/.test(normalizedPart)) {
      arrayPaths.add(previousPath);
      return;
    }

    const arrayMatch = normalizedPart.match(/^(.*?)(?:\[\d*\])$/);
    const part = sanitizeConditionPathPart(arrayMatch ? arrayMatch[1] : normalizedPart);
    if (!part) return;
    parts.push(part);
    previousPath = parts.join('.');
    if (arrayMatch) arrayPaths.add(previousPath);
  });

  const path = parts.join('.');
  if (path && isArrayLikeType(type)) arrayPaths.add(path);
  return { path, arrayPaths: [...arrayPaths] };
};

const normalizeConditionVariablePath = (value) => conditionVariablePathMetadata(value).path;

const buildConditionVariableTree = (variables = [], preserveValue = '') => {
  const root = { path: '', name: '', children: new Map(), isArray: false, option: null };
  const displayNameByPath = new Map();
  const normalizeDisplayName = (variable = {}) => {
    const displayName = cleanText(variable?.displayName || variable?.display_name || '');
    return displayName || '';
  };
  const addPath = (path, option = null, arrayPaths = []) => {
    if (!path) return;
    let node = root;
    const parts = conditionPathSegments(path);
    parts.forEach((name, index) => {
      const nextPath = parts.slice(0, index + 1).join('.');
      if (!node.children.has(name)) {
        node.children.set(name, { path: nextPath, name, children: new Map(), isArray: false, option: null });
      }
      node = node.children.get(name);
      if (arrayPaths.includes(nextPath)) node.isArray = true;
    });
    if (option) {
      const currentType = cleanText(node.option?.type);
      const nextType = cleanText(option?.type);
      if (!node.option || (!currentType && nextType)) {
        node.option = option;
      }
    }
  };

  variables.forEach((variable) => {
    const metadata = conditionVariablePathMetadata(variable?.value, variable?.type);
    const displayName = normalizeDisplayName(variable);
    if (metadata.path && displayName) {
      displayNameByPath.set(metadata.path, displayName);
    }
    const extraArrayPaths = Array.isArray(variable?.arrayPaths) ? variable.arrayPaths : [];
    const arrayPaths = [...new Set([...metadata.arrayPaths, ...extraArrayPaths].map((path) => normalizeRootNamespacePath(path)))];
    if (arrayPaths.includes('')) root.isArray = true;
    if (metadata.path) addPath(metadata.path, { ...variable, value: metadata.path }, arrayPaths);
  });

  const preserved = conditionVariablePathMetadata(preserveValue);
  const normalizedPreservedArrayPaths = preserved.arrayPaths.map((path) => normalizeRootNamespacePath(path));
  if (normalizedPreservedArrayPaths.includes('')) root.isArray = true;
  if (preserved.path) addPath(preserved.path, { value: preserved.path, type: '' }, normalizedPreservedArrayPaths);

  const finalize = (node) => {
    const children = [...node.children.values()]
      .map(finalize)
      .sort((left, right) => left.name.localeCompare(right.name, 'pt-BR'));
    const type = node.isArray ? 'array' : node.option?.type || (children.length ? 'object' : '');
    const displayName = displayNameByPath.get(node.path) || node.name;
    return {
      path: node.path,
      name: node.name,
      type,
      isArray: node.isArray,
      label: node.path ? `${displayName} (${conditionVariableTypeLabel(type)})` : '',
      children
    };
  };

  return finalize(root);
};

const findConditionVariableNode = (tree, path) => {
  const normalizedPath = normalizeConditionVariablePath(path);
  if (!normalizedPath) return null;
  return conditionPathSegments(normalizedPath).reduce((node, name) => (
    node?.children.find((child) => child.name === name) || null
  ), tree);
};

const conditionVariableSelectionLevels = (variables = [], selectedValue = '') => {
  const tree = buildConditionVariableTree(variables, selectedValue);
  const selectedParts = conditionPathSegments(normalizeConditionVariablePath(selectedValue));
  const levels = [];
  let node = tree;
  let depth = 0;

  while (node.children.length) {
    const selectedNode = selectedParts[depth]
      ? node.children.find((child) => child.name === selectedParts[depth]) || null
      : null;
    levels.push({
      parentPath: node.path,
      label: node.path
        ? node.isArray
          ? `Atributo do item de ${node.path}`
          : `Atributo de ${node.path}`
        : 'Atributo',
      value: selectedNode?.path || '',
      options: node.children
    });
    if (!selectedNode?.children.length) break;
    node = selectedNode;
    depth += 1;
  }

  return levels;
};

const conditionArrayPathsForVariable = (variables = [], value = '') => {
  const tree = buildConditionVariableTree(variables, value);
  const normalizedPath = normalizeRootNamespacePath(normalizeConditionVariablePath(value));
  const paths = tree.isArray ? [''] : [];
  let node = tree;

  conditionPathSegments(normalizedPath).forEach((part) => {
    node = node?.children.find((child) => child.name === part) || null;
    if (node?.isArray) paths.push(node.path);
  });

  return paths;
};

const conditionVariableMetadata = (variables = [], value = '') => {
  const tree = buildConditionVariableTree(variables, value);
  return findConditionVariableNode(tree, value);
};

const conditionArrayDescendantOptions = (variables = [], arrayPath = '') => {
  const tree = buildConditionVariableTree(variables, arrayPath);
  const arrayNode = arrayPath ? findConditionVariableNode(tree, arrayPath) : tree;
  if (!arrayNode) return [];
  const options = [];

  const visit = (node, relativeParts = [], arrayPaths = []) => {
    node.children.forEach((child) => {
      const nextParts = relativeParts.concat(child.name);
      const relativePath = nextParts.join('.');
      const nextArrayPaths = child.isArray
        ? arrayPaths.concat(relativePath)
        : arrayPaths;
      options.push({
        value: relativePath,
        label: `${relativePath} (${conditionVariableTypeLabel(child.type)})`,
        type: child.type,
        arrayPaths: nextArrayPaths
      });
      visit(child, nextParts, nextArrayPaths);
    });
  };

  visit(arrayNode);
  return options.sort((left, right) => left.value.localeCompare(right.value, 'pt-BR'));
};

module.exports = {
  conditionArrayDescendantOptions,
  conditionArrayPathsForVariable,
  conditionVariableMetadata,
  conditionVariablePathMetadata,
  conditionVariableSelectionLevels,
  normalizeConditionVariablePath
};
